import { create } from 'zustand';
import { api } from '../services/api';
import type { EditRateData } from '../types';

interface QuickApproveState {
  prefs: Record<string, boolean>;
  editRates: Record<string, EditRateData>;
  loaded: boolean;
  nudgeDismissed: Record<string, boolean>;
  // Set when a prefs / edit-rate PUT fails; the optimistic local change is
  // rolled back and this surfaces the failure. Cleared on the next successful
  // mutation. (M10 fix, 2026-07-05.)
  saveError: string | null;
  clearSaveError: () => void;
  fetchPrefs: () => Promise<void>;
  toggleQuickApprove: (taskName: string, enabled: boolean) => Promise<void>;
  isQuickApproveOn: (taskName: string) => boolean;
  recordApproval: (taskName: string, hadEdits: boolean) => Promise<void>;
  getEditRate: (taskName: string) => EditRateData | null;
  shouldShowNudge: (taskName: string) => boolean;
  dismissNudge: (taskName: string) => Promise<void>;
  fetchEditRates: () => Promise<void>;
}

export const useQuickApproveStore = create<QuickApproveState>()((set, get) => ({
  prefs: {},
  editRates: {},
  loaded: false,
  nudgeDismissed: {},
  saveError: null,

  clearSaveError: () => set({ saveError: null }),

  fetchPrefs: async () => {
    try {
      const data = await api.get('/api/config/quick-approve') as Record<string, boolean> | null;
      set({ prefs: data || {}, loaded: true });
    } catch {
      set({ loaded: true });
    }
  },

  toggleQuickApprove: async (taskName: string, enabled: boolean) => {
    const prevPrefs = get().prefs;
    const prefs = { ...prevPrefs, [taskName]: enabled };
    set({ prefs, saveError: null });
    try {
      await api.put('/api/config/quick-approve', prefs);
    } catch (err) {
      // Revert the optimistic toggle and surface the failure.
      set({ prefs: prevPrefs, saveError: (err as Error).message || 'Failed to save preference.' });
    }
  },

  isQuickApproveOn: (taskName: string) => get().prefs[taskName] === true,

  recordApproval: async (taskName: string, hadEdits: boolean) => {
    const prevRates = get().editRates;
    const prev: EditRateData = prevRates[taskName] || {
      total_approvals: 0,
      approvals_with_edits: 0,
      last_edit_date: null,
      consecutive_no_edit_approvals: 0,
    };

    // Immutable update — build a fresh object so zustand consumers re-render
    // (L20 fix). Never mutate the previously-stored EditRateData in place.
    const current: EditRateData = {
      ...prev,
      total_approvals: prev.total_approvals + 1,
      ...(hadEdits
        ? {
            approvals_with_edits: prev.approvals_with_edits + 1,
            last_edit_date: new Date().toISOString(),
            consecutive_no_edit_approvals: 0,
          }
        : {
            consecutive_no_edit_approvals: prev.consecutive_no_edit_approvals + 1,
          }),
    };

    set({ editRates: { ...prevRates, [taskName]: current }, saveError: null });

    try {
      await api.put(`/api/config/edit-rates/${taskName}`, current);
    } catch (err) {
      // Revert the optimistic bump and surface the failure.
      set({ editRates: prevRates, saveError: (err as Error).message || 'Failed to save approval.' });
    }
  },

  getEditRate: (taskName: string) => get().editRates[taskName] || null,

  shouldShowNudge: (taskName: string) => {
    const rate = get().editRates[taskName];
    if (!rate) return false;
    if (get().prefs[taskName]) return false;
    if (get().nudgeDismissed[taskName]) return false;
    return rate.consecutive_no_edit_approvals >= 20;
  },

  dismissNudge: async (taskName: string) => {
    set((s) => ({ nudgeDismissed: { ...s.nudgeDismissed, [taskName]: true }, saveError: null }));
    const prevRates = get().editRates;
    const prev = prevRates[taskName];
    if (prev) {
      // Immutable update — fresh object, no in-place mutation (L20 fix).
      const current: EditRateData = {
        ...prev,
        consecutive_no_edit_approvals: 0,
        nudge_dismissed_at: new Date().toISOString(),
      };
      set({ editRates: { ...prevRates, [taskName]: current } });
      try {
        await api.put(`/api/config/edit-rates/${taskName}`, current);
      } catch (err) {
        // Revert the edit-rate reset and surface the failure. The local nudge
        // dismissal stays (a UI-only preference), but the counter rolls back so
        // it stays consistent with the server.
        set({ editRates: prevRates, saveError: (err as Error).message || 'Failed to save nudge dismissal.' });
      }
    }
  },

  fetchEditRates: async () => {
    try {
      const data = await api.get('/api/config/edit-rates') as Record<string, EditRateData> | null;
      set({ editRates: data || {} });
    } catch {
      // best-effort
    }
  },
}));
